import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { EMPTY_ARRAY_LENGTH } from '@modeso/aumico-lib-core-fe';
import { INameId } from '@modeso/types__aumico-lib-core-be';
import {
    IGroupEntityDTO,
    ITemplateGroupEntityDTO,
    ITemplateGroupItemEntityDTO,
    ITemplateGroupMappingItemSubFli,
    ItemType,
    SortedTemplateItem,
    TemplateType,
} from '@modeso/types__aumico-lib-templates-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ofType } from '@ngrx/effects';
import { ActionsSubject } from '@ngrx/store';
import { I18NextService } from 'angular-i18next';
import Debug from 'debug';
import { cloneDeep } from 'lodash-es';
import { Observable, Subject } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { onCreateGroupSuccessfully } from '../../actions/templates.actions';
import { AdminSubFliFormDialog } from '../../components/admin-sub-fli-form-dialog/sub-fli-form-dialog.component';
import { BaseComponent } from '../../components/base.component';
import { FinancialLineItemsListComponent } from '../../components/groups-items/financial-line-items-list/financial-line-items-list.component';
import { SubtotalFormComponent } from '../../components/groups-items/subtotal-form/subtotal-form.component';
import { GroupFormComponent } from '../../components/groups/group-form/group-form.component';
import { GroupListComponent } from '../../components/groups/group-list/group-list.component';
import { AS_FIRST_ELEMENT_OPTION_ID } from '../../constants/subtotal-form.constant';
import { SortingTemplateItemsHelper } from '../../helpers/sorting-mapper.helper';
import { IFinancialLineItem } from '../../interfaces/settings.interface';
import { ITemplatesSubtotalFormData } from '../../interfaces/templates-subtotal-form-data.interface';
import { ITemplatesSubtotalFormResult } from '../../interfaces/templates-subtotal-form-result.interface';
import { TemplateGroup } from '../../models/template.model';
import { TemplatesProvider } from '../../provider/templates.provider';

const debug = Debug('aumico:aumico-lib-templates-fe:GroupViewComponent');
const ONE = 1;
@UntilDestroy()
@Component({
    selector: 'templates-group',
    templateUrl: 'group-view.component.html',
    styleUrls: ['group-view.component.scss'],
})
export class GroupViewComponent extends BaseComponent implements OnInit {
    @Input() templateGroup: ITemplateGroupEntityDTO;
    @Input() parentGroup: ITemplateGroupEntityDTO;
    @Input() templateType: TemplateType;

    @Input() currentLanguage = 'de-ch';
    @Input() financialLineItems: IFinancialLineItem[] = [];
    @Input() groupIsLast: boolean;
    @Input() groupIsFirst: boolean;
    @Input() languages: string[];
    @Output() removeTemplateGroupEvent = new EventEmitter<ITemplateGroupEntityDTO>();
    @Output() removeFromEquityEvent = new EventEmitter<ITemplateGroupEntityDTO>();
    @Output() addTemplateGroupEvent = new EventEmitter<ITemplateGroupEntityDTO>();
    @Output() onGroupSortingClicked = new EventEmitter<{ itemToMove: SortedTemplateItem; up: boolean }>();
    groups$: Observable<IGroupEntityDTO[]>;
    private selectedGroup: ITemplateGroupEntityDTO | undefined;
    private hiddenGroupCount = 1;

    constructor(
        public override templatesProvider: TemplatesProvider,
        private dialog: MatDialog,
        private readonly actionsListener$: ActionsSubject,
        private readonly localizedTexts: I18NextService,
    ) {
        super(templatesProvider);
    }

    override ngOnInit() {
        super.ngOnInit();
        this.actionsListener$.pipe(ofType(onCreateGroupSuccessfully), untilDestroyed(this)).subscribe(({ payload }) => {
            if (this.selectedGroup == null) {
                return;
            }
            const addedGroup = new TemplateGroup(payload);
            this.templateGroup.groups.push(addedGroup);
            this.addTemplateGroupEvent.emit(addedGroup);
            (this.templateGroup.sortedItems as SortedTemplateItem[]).push(addedGroup);
            this.hiddenGroupCount++;
            this.selectedGroup = null;
        });
        this.groups$ = this.templatesProvider.getGroups$();
    }

    private getHiddenGroupsCount(): void {
        const hiddenGroups = this.templateGroup.groups.filter((group) => group.groupRef.isHiddenGroup);
        if (hiddenGroups != null) {
            this.hiddenGroupCount = hiddenGroups.length + 1;
        }
    }

    addTemplateGroups() {
        let activeGroupResult: IGroupEntityDTO;
        let groupNameExistsError = new Subject<string>();

        const dialogRef = this.dialog.open(GroupListComponent, {
            width: '500px',
            disableClose: true,
            data: { groupNameExistsError: groupNameExistsError },
        });

        if (dialogRef.componentInstance != null) {
            dialogRef.componentInstance.groups$ = this.groups$;
        }

        dialogRef.componentInstance.editGroupClicked.subscribe((groupId: string) => {
            this.templatesProvider.setActiveGroup(groupId);
            this.templatesProvider.getActiveGroup$().subscribe((activeGroup: IGroupEntityDTO) => {
                activeGroupResult = activeGroup;
                dialogRef.componentInstance.activeGroup = activeGroupResult;
            });
        });

        dialogRef.componentInstance.checkIfGroupNameExists.subscribe((group: IGroupEntityDTO) => {
            this.templatesProvider
                .getGroupNameExists$(group.name, group._id)
                .pipe(take(1))
                .subscribe((groupNameExist: boolean) => {
                    if (groupNameExist) {
                        groupNameExistsError.next('aumico_templates_group_form_name_already_exists_error');
                    } else {
                        groupNameExistsError.next(null);
                    }
                });
        });

        dialogRef.componentInstance.createGroup.subscribe((group: IGroupEntityDTO) => {
            this.templatesProvider.createGroup(group);
        });

        dialogRef.componentInstance.updateGroup.subscribe((group: IGroupEntityDTO) => {
            this.templatesProvider.updateGroup(group, group._id);
        });

        dialogRef.componentInstance.deleteGroupClicked.subscribe((groupId: string) => {
            this.templatesProvider.deleteGroup(groupId);
        });

        dialogRef.componentInstance.editGroupFormClosed.subscribe((formClosed: boolean) => {
            if (formClosed) {
                this.templatesProvider.removeActiveGroup();
            }
        });

        this.subscriptions.push(
            dialogRef.afterClosed().subscribe((selectedGroups: IGroupEntityDTO[]) => {
                this.templatesProvider.removeActiveGroup();
                if (selectedGroups && selectedGroups.length) {
                    const selectedGroupsIds: string[] = selectedGroups.map((group) => group._id);
                    this.templatesProvider.selectGroups(selectedGroupsIds);
                    for (const group of selectedGroups) {
                        const groupToBeAdded = new TemplateGroup(group);
                        this.templateGroup.groups.push(groupToBeAdded);
                        (this.templateGroup.sortedItems as SortedTemplateItem[]).push(groupToBeAdded);
                        this.addTemplateGroupEvent.emit(groupToBeAdded);
                    }
                }
            }),
        );
    }

    public addSubtotal(): void {
        const subtotalPositionOptions = this.preparePositionOptionsForSubtotal();
        const subtotalScopeOptions = this.prepareScopeOptionsForSubtotal();
        const allItemsInGroup = cloneDeep(this.templateGroup.sortedItems);

        const dialogRef = this.dialog.open(SubtotalFormComponent, {
            width: '450px',
            disableClose: true,
            data: {
                languages: this.languages,
                positionOptions: subtotalPositionOptions,
                scopeOptions: subtotalScopeOptions,
                allItemsInGroup: allItemsInGroup,
            } as ITemplatesSubtotalFormData,
        });

        dialogRef.componentInstance.savePressed.subscribe((subtotalName) => {
            this.templatesProvider
                .getSubtotalNameExists$(subtotalName)
                .pipe(take(1))
                .subscribe((subtotalNameExist: boolean) => {
                    dialogRef.componentInstance.subtotalNameExists = subtotalNameExist;
                });
        });

        this.subscriptions.push(
            dialogRef.afterClosed().subscribe((result: ITemplatesSubtotalFormResult) => {
                if (result != null) {
                    const subtotalToBeAdded: ITemplateGroupItemEntityDTO = {
                        name: result.name,
                        isSubtotal: true,
                        description: result.description,
                        scope: { name: result.scope.id, type: ItemType.GROUP },
                    };

                    const placeSubtotalAsFirstItem = result.positionBeforeSubtotal.id === AS_FIRST_ELEMENT_OPTION_ID;
                    // Ensure the index is within bounds
                    if (
                        result.positionBeforeSubtotalIndex == null ||
                        (!placeSubtotalAsFirstItem && result.positionBeforeSubtotalIndex < 0) ||
                        result.positionBeforeSubtotalIndex >= this.templateGroup.sortedItems.length
                    ) {
                        throw new Error('Invalid index provided for insertion.');
                    }

                    this.templateGroup.items.push(subtotalToBeAdded);

                    if (placeSubtotalAsFirstItem) {
                        (this.templateGroup.sortedItems as SortedTemplateItem[]).unshift(subtotalToBeAdded);
                    } else {
                        (this.templateGroup.sortedItems as SortedTemplateItem[]).splice(
                            result.positionBeforeSubtotalIndex + 1,
                            0,
                            subtotalToBeAdded,
                        );
                    }

                    this.templatesProvider.setSubtotalsNames([result.name]);
                }
            }),
        );
    }

    public editSubtotal(subtotalToEdit: ITemplateGroupItemEntityDTO): void {
        const subtotalPositionOptions = this.preparePositionOptionsForSubtotal();
        const subtotalScopeOptions = this.prepareScopeOptionsForSubtotal();
        const allItemsInGroup = cloneDeep(this.templateGroup.sortedItems);
        const selectedScope = subtotalScopeOptions.find((option) => option.id === subtotalToEdit.scope.name);

        const subtotalIndexInSortedItems = this.templateGroup.sortedItems.findIndex((sortedItem) => {
            return sortedItem.name === subtotalToEdit.name && sortedItem.isSubtotal;
        });
        const subtotalIndexInItems = this.templateGroup.items.findIndex((sortedItem) => {
            return sortedItem.name === subtotalToEdit.name && sortedItem.isSubtotal;
        });

        const positionBeforeSubtotalObject = this.templateGroup.sortedItems[subtotalIndexInSortedItems - 1];

        let selectedPositionBeforeSubtotal: INameId | undefined;
        if (positionBeforeSubtotalObject != null) {
            if (SortingTemplateItemsHelper.isTemplateGroup(positionBeforeSubtotalObject as SortedTemplateItem)) {
                selectedPositionBeforeSubtotal = subtotalPositionOptions.find(
                    (option) =>
                        option.name ===
                            (positionBeforeSubtotalObject as ITemplateGroupEntityDTO).groupRef.title[
                                this.currentLanguage
                            ] && option.id === (positionBeforeSubtotalObject as ITemplateGroupEntityDTO).groupRef.name,
                );
            } else {
                selectedPositionBeforeSubtotal = subtotalPositionOptions.find(
                    (option) =>
                        option.name ===
                            (positionBeforeSubtotalObject as ITemplateGroupItemEntityDTO).description[
                                this.currentLanguage
                            ] && option.id === (positionBeforeSubtotalObject as ITemplateGroupItemEntityDTO).name,
                );
            }
        } else {
            selectedPositionBeforeSubtotal = {
                id: AS_FIRST_ELEMENT_OPTION_ID,
                name: this.localizedTexts.t('aumico_templates_subtotal_select_position_as_first_element') as string,
            };
        }

        const dialogRef = this.dialog.open(SubtotalFormComponent, {
            width: '450px',
            disableClose: true,
            data: {
                languages: this.languages,
                positionOptions: subtotalPositionOptions,
                scopeOptions: subtotalScopeOptions,
                allItemsInGroup: allItemsInGroup,
                savedSubtotal: {
                    name: subtotalToEdit.name,
                    description: subtotalToEdit.description,
                    scope: {
                        name: selectedScope?.name,
                        id: selectedScope?.id,
                    },
                    positionBeforeSubtotal: selectedPositionBeforeSubtotal,
                },
            } as ITemplatesSubtotalFormData,
        });

        dialogRef.componentInstance.savePressed.subscribe((subtotalName) => {
            this.templatesProvider
                .getSubtotalNameExists$(subtotalName)
                .pipe(take(1))
                .subscribe((subtotalNameExist: boolean) => {
                    if (subtotalName !== subtotalToEdit.name) {
                        dialogRef.componentInstance.subtotalNameExists = subtotalNameExist;
                    } else {
                        dialogRef.componentInstance.subtotalNameExists = false;
                    }
                });
        });

        this.subscriptions.push(
            dialogRef.afterClosed().subscribe((result: ITemplatesSubtotalFormResult) => {
                if (result != null) {
                    const updatedSubtotal: ITemplateGroupItemEntityDTO = {
                        name: result.name,
                        isSubtotal: true,
                        description: result.description,
                        scope: { name: result.scope.id, type: ItemType.GROUP },
                    };

                    if (
                        result.positionBeforeSubtotalIndex == null ||
                        result.positionBeforeSubtotalIndex >= this.templateGroup.sortedItems.length
                    ) {
                        throw new Error('Invalid index provided for insertion.');
                    }

                    this.templateGroup.items.splice(subtotalIndexInItems, 1);
                    this.templateGroup.items.push(updatedSubtotal);

                    this.templateGroup.sortedItems.splice(subtotalIndexInSortedItems, 1);

                    if (result.positionBeforeSubtotalIndex <= subtotalIndexInSortedItems) {
                        this.templateGroup.sortedItems.splice(
                            result.positionBeforeSubtotalIndex + 1,
                            0,
                            updatedSubtotal,
                        );
                    } else {
                        this.templateGroup.sortedItems.splice(result.positionBeforeSubtotalIndex, 0, updatedSubtotal);
                    }

                    this.templatesProvider.setSubtotalsNames([result.name]);
                }
            }),
        );
    }

    private preparePositionOptionsForSubtotal(): INameId[] {
        return (this.templateGroup.sortedItems as SortedTemplateItem[])
            .filter((item) => SortingTemplateItemsHelper.isTemplateGroup(item) || !item.isSubtotal)
            .map((sortedItem) => {
                return {
                    id: SortingTemplateItemsHelper.isTemplateGroup(sortedItem)
                        ? sortedItem.groupRef.name
                        : sortedItem.name,
                    name: SortingTemplateItemsHelper.isTemplateGroup(sortedItem)
                        ? sortedItem.groupRef.title[this.currentLanguage]
                        : sortedItem.description[this.currentLanguage],
                } as INameId;
            });
    }

    private prepareScopeOptionsForSubtotal(): INameId[] {
        const path: ITemplateGroupEntityDTO[] = [];

        this.getItemParentGroupAndGrandparentsGroups(this.parentGroup, this.templateGroup, path);

        // The path is from target to root, reverse it to get root to target
        const groupOptions = path.reverse().map((group: ITemplateGroupEntityDTO): INameId => {
            return {
                id: group.groupRef.name,
                name: group.groupRef.title[this.currentLanguage],
            };
        });
        if (this.templateType === TemplateType.BALANCE) {
            // remove balance sheet group from options
            groupOptions.shift();
        }
        return groupOptions;
    }

    private getItemParentGroupAndGrandparentsGroups(
        root: ITemplateGroupEntityDTO,
        targetItem: ITemplateGroupEntityDTO,
        path: ITemplateGroupEntityDTO[],
    ): boolean {
        // this function is to get the parent group and all the grandparents groups
        if (root === targetItem) {
            path.push(root);
            return true;
        }
        for (const child of root.groups) {
            if (this.getItemParentGroupAndGrandparentsGroups(child, targetItem, path)) {
                // Add the parent to the path
                path.push(root);
                return true;
            }
        }
        return false;
    }

    addFinancialLineItems() {
        const dialogRef = this.dialog.open(FinancialLineItemsListComponent, {
            width: '500px',
            disableClose: true,
            data: { financialLineItems: this.financialLineItems },
        });
        this.subscriptions.push(
            dialogRef.afterClosed().subscribe((financialLineItems: IFinancialLineItem[]) => {
                if (financialLineItems && financialLineItems.length) {
                    this.templatesProvider.selectFinancialLineItems(financialLineItems.map((fli) => fli.name));
                    for (const fli of financialLineItems) {
                        const fliToBeAdded = {
                            name: fli.name,
                            isSubtotal: false,
                            type: fli.type,
                            description: fli.description,
                            fliObj: fli,
                            _id: fli._id,
                        };
                        this.templateGroup.items.push(fliToBeAdded);
                        (this.templateGroup.sortedItems as SortedTemplateItem[]).push(fliToBeAdded);
                    }
                }
            }),
        );
    }

    public addHiddenGroup(): void {
        this.getHiddenGroupsCount();
        const timestamp = Date.now();
        const timestampString = timestamp.toString();
        this.selectedGroup = this.templateGroup;
        const hiddenGroup: IGroupEntityDTO = {
            name: 'hiddenGroup-' + timestampString,
            title: {
                'de-ch':
                    'hidden Group' + '-' + this.templateGroup.groupRef.title['de-ch'] + '-' + this.hiddenGroupCount,
            },
            totalName: { 'de-ch': 'hidden Group' },
            hideTotal: false,
            allowItems: true,
            allowGroups: true,
            notDeletable: false,
            isDefault: false,
            isHiddenGroup: true,
        };
        this.templatesProvider.createGroup(hiddenGroup);
        this.addTemplateGroupEvent.emit(new TemplateGroup(hiddenGroup));
    }

    public moveGroup(groupItem: ITemplateGroupEntityDTO, up: boolean): void {
        this.onGroupSortingClicked.emit({ itemToMove: groupItem, up });
    }

    public moveSubFli(
        subfliToMove: ITemplateGroupMappingItemSubFli,
        parentItem: ITemplateGroupItemEntityDTO,
        up: boolean,
    ): void {
        const index = parentItem.subFlisAndOfThatItems.findIndex((subfli) => subfli.name === subfliToMove.name);

        if (index == null) {
            throw new Error('cannot find subfli with matching name in parent fli');
        }

        const replacedIndex = up ? index - ONE : index + ONE;

        if (replacedIndex < EMPTY_ARRAY_LENGTH || replacedIndex > this.templateGroup.sortedItems.length - ONE) {
            return;
        }

        const temp = parentItem.subFlisAndOfThatItems[replacedIndex];
        parentItem.subFlisAndOfThatItems[replacedIndex] = parentItem.subFlisAndOfThatItems[index];
        parentItem.subFlisAndOfThatItems[index] = temp;
    }

    public moveItem(itemToMove: SortedTemplateItem, up: boolean): void {
        let index: number;
        if (SortingTemplateItemsHelper.isTemplateGroup(itemToMove as SortedTemplateItem)) {
            index = this.templateGroup.sortedItems.findIndex(
                (sortedItemGroup) =>
                    sortedItemGroup.groupRef?._id === (itemToMove as ITemplateGroupEntityDTO).groupRef._id,
            );
        } else {
            index = this.templateGroup.sortedItems.findIndex(
                (sortedItem) => sortedItem.name === (itemToMove as ITemplateGroupItemEntityDTO).name,
            );
        }

        const replacedIndex = up ? index - ONE : index + ONE;

        if (replacedIndex < EMPTY_ARRAY_LENGTH || replacedIndex > this.templateGroup.sortedItems.length - ONE) {
            return;
        }
        const temp = this.templateGroup.sortedItems[replacedIndex];
        this.templateGroup.sortedItems[replacedIndex] = this.templateGroup.sortedItems[index];
        this.templateGroup.sortedItems[index] = temp;
    }

    private deselectGroupItem(groupItem: ITemplateGroupItemEntityDTO): void {
        if (groupItem.isSubtotal == false) {
            this.templatesProvider.deselectFinancialLineItem(groupItem.name);
        } else if (groupItem.isSubtotal == true) {
            this.templatesProvider.removeSubtotalName(groupItem.name);
        }
    }

    removeGroupItem(groupItem: ITemplateGroupItemEntityDTO) {
        this.deselectGroupItem(groupItem);
        const index = this.templateGroup.items.findIndex(
            (item) => item.name == groupItem.name && item.isSubtotal == groupItem.isSubtotal,
        );
        const sortedItemIndex = this.templateGroup.sortedItems.findIndex(
            (item) => item.name == groupItem.name && item.isSubtotal == groupItem.isSubtotal,
        );

        this.templateGroup.items.splice(index, 1);
        this.templateGroup.sortedItems.splice(sortedItemIndex, 1);
    }

    removeSubFli(index: number, fli: ITemplateGroupItemEntityDTO) {
        const fliIndex = this.templateGroup.items.findIndex((item) => item.name === fli.name);
        this.templateGroup.items[fliIndex].subFlisAndOfThatItems?.splice(index, 1);
    }

    deselectTemplateGroup(templateGroup: ITemplateGroupEntityDTO) {
        if (templateGroup.items.length) {
            for (let item of templateGroup.items) {
                this.deselectGroupItem(item);
            }
        }
        if (templateGroup.groups.length) {
            for (let group of templateGroup.groups) {
                this.deselectTemplateGroup(group);
            }
        }
        this.templatesProvider.deselectGroup(templateGroup.groupRef._id);
    }

    removeTemplateGroup(templateGroupToBeRemoved: ITemplateGroupEntityDTO) {
        this.deselectTemplateGroup(templateGroupToBeRemoved);
        const index = this.templateGroup.groups.findIndex(
            (gp) => gp.groupRef._id == templateGroupToBeRemoved.groupRef._id,
        );
        const sortedItemIndex = this.templateGroup.sortedItems.findIndex(
            (gp) => gp.groupRef?._id == templateGroupToBeRemoved.groupRef._id,
        );

        this.templateGroup.groups.splice(index, 1);
        this.templateGroup.sortedItems.splice(sortedItemIndex, 1);

        this.removeFromEquityEvent.emit(templateGroupToBeRemoved);
    }

    updateDefaultGroup(defaultGroup: IGroupEntityDTO): void {
        let activeGroupResult: IGroupEntityDTO;

        this.templatesProvider.setActiveGroup(defaultGroup._id);
        this.templatesProvider.getActiveGroup$().subscribe((activeGroup: IGroupEntityDTO) => {
            activeGroupResult = activeGroup;
        });

        const dialogRef = this.dialog.open(GroupFormComponent, {
            width: '385px',
            disableClose: true,
            data: { activeGroup: activeGroupResult },
        });

        dialogRef.componentInstance.updateGroup.subscribe((group) => {
            this.templatesProvider.updateGroup(group, group._id);
        });

        dialogRef.componentInstance.createGroup.subscribe((group) => {
            this.templatesProvider.createGroup(group);
        });

        this.templatesProvider
            .getDefaultGroups$()
            .pipe(
                filter((defaultGroups: Map<string, IGroupEntityDTO>) => defaultGroups != null),
                untilDestroyed(this),
            )
            .subscribe((defaultGroups: Map<string, IGroupEntityDTO>) => {
                defaultGroup.title = defaultGroups.get(defaultGroup.name).title;
                defaultGroup.totalName = defaultGroups.get(defaultGroup.name).totalName;
            });

        dialogRef
            .afterClosed()
            .pipe(untilDestroyed(this))
            .subscribe(() => this.templatesProvider.removeActiveGroup());
    }

    public editSubFli(fli: ITemplateGroupItemEntityDTO, subFli: ITemplateGroupMappingItemSubFli, index: number): void {
        const dialogRef = this.dialog.open(AdminSubFliFormDialog, {
            width: '500px',
            data: { languages: this.languages, subfliToBeEdited: subFli },
        });

        dialogRef
            .afterClosed()
            .pipe(
                filter((result) => result != undefined),
                untilDestroyed(this),
            )
            .subscribe((result: ITemplateGroupMappingItemSubFli) => {
                const fliIndex = this.templateGroup.items.findIndex((item) => item.name === fli.name);
                const fliIndexInSortedItems = this.templateGroup.sortedItems.findIndex((item) => item._id === fli._id);
                if (fliIndex === -1) {
                    throw new Error('No Fli found with that name in this group');
                }

                const fliInItems = this.templateGroup.items[fliIndex];
                const fliInSortedItems = (this.templateGroup.sortedItems as SortedTemplateItem[])[
                    fliIndexInSortedItems
                ] as ITemplateGroupItemEntityDTO;

                fliInSortedItems.subFlisAndOfThatItems[index] = result;
                fliInItems.subFlisAndOfThatItems[index] = result;
            });
    }

    public addSubFli(fli: ITemplateGroupItemEntityDTO): void {
        const dialogRef = this.dialog.open(AdminSubFliFormDialog, {
            width: '500px',
            data: { languages: this.languages },
        });

        dialogRef
            .afterClosed()
            .pipe(
                filter((result) => result != undefined),
                untilDestroyed(this),
            )
            .subscribe((result: ITemplateGroupMappingItemSubFli) => {
                const fliIndex = this.templateGroup.items.findIndex((item) => item.name === fli.name);
                const fliIndexInSortedItems = this.templateGroup.sortedItems.findIndex((item) => item._id === fli._id);
                if (fliIndex == null) {
                    throw new Error('No Fli found with that name in this group');
                }
                let subFlisAndOfThatItems = this.templateGroup.items[fliIndex].subFlisAndOfThatItems;
                if (subFlisAndOfThatItems != null) {
                    subFlisAndOfThatItems.push(result);
                } else {
                    subFlisAndOfThatItems = [result];
                }
                const fliInSortedItems = (this.templateGroup.sortedItems as SortedTemplateItem[])[
                    fliIndexInSortedItems
                ] as ITemplateGroupItemEntityDTO;

                fliInSortedItems.subFlisAndOfThatItems = subFlisAndOfThatItems;
            });
    }

    onAddGroupTemplate(group: ITemplateGroupEntityDTO): void {
        this.addTemplateGroupEvent.emit(group);
    }
}
